import Header from "./Header";
import Footer from "./Footer";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { fetchMemberships } from "../redux/membershipSlice";
import { getUserFromToken } from "../function";
import { useNavigate } from "react-router-dom";
import { server } from "../server";
import { toast } from "react-toastify";
import { useLocation } from "react-router-dom";

function Pricing() {
  const dispatch = useDispatch();
  const location = useLocation();
  const user = getUserFromToken();
  const { loading, plans, error } = useSelector((state) => state.memberships);
  const SKIP_ID = "689eea2ef6d3560a27be13c3";
  const regularPlans = plans
    .filter((p) => p.category === "Regular" && p._id !== SKIP_ID)
    .sort((a, b) => Number(a.index) - Number(b.index));

  const basicPlans = plans
    .filter((p) => p.category === "Basic" && p._id !== SKIP_ID)
    .sort((a, b) => Number(a.index) - Number(b.index));

  const navigate = useNavigate();
  const [isPaying, setIsPaying] = useState(false);
  const [payingPlanId, setPayingPlanId] = useState(null);
  const [activeTab, setActiveTab] = useState("Regular");
  const GST_RATE = 0.18;
  const getGstAmount = (price) => Math.round(price * GST_RATE);
  const getTotalWithGst = (price) => price + getGstAmount(price);
  const [showCouponModal, setShowCouponModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [couponCode, setCouponCode] = useState("");

  const USD_RATE = 90;
  const inrToUsd = (inr) => Math.round(inr / USD_RATE);
  const formatUSD = (val) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(val);

  useEffect(() => {
    if (location.pathname.includes("/event-plan")) {
      setActiveTab("Basic");
    } else {
      setActiveTab("Regular");
    }
  }, [location.pathname]);

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    document.body.appendChild(script);
  }, []);

  const proceedToPayment = async (useCoupon) => {
    setShowCouponModal(false);

    const payload = {
      userObjectId: user._id,
      membershipId: selectedPlan._id,
    };

    if (useCoupon && couponCode.trim()) {
      payload.couponCode = couponCode.trim();
    }

    // 🔁 MOVE your existing CreateOrder logic here
    await handleBuyPlanWithPayload(selectedPlan, payload);
  };

  const handleBuyPlanWithPayload = async (plan, payload) => {
    try {
      setPayingPlanId(plan._id);

      const orderRes = await fetch(`${server}/Payment/CreateOrder`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: localStorage.getItem("authToken"),
        },
        body: JSON.stringify(payload),
      });

      const order = await orderRes.json();

      if (!order?.order?.id) {
        toast.error(order?.message || "Failed to create order");
        setPayingPlanId(null);
        return;
      }

      // ⬇️ Razorpay code stays EXACTLY SAME ⬇️
      openRazorpay(order, plan);
    } catch (err) {
      setPayingPlanId(null);
      toast.error("Payment Failed");
    }
  };

  const openRazorpay = (order, plan) => {
    const options = {
      key: "rzp_live_RiSuGkazTIimf5",
      amount: order.order.amount,
      currency: order.order.currency,
      image: "/assets/images/logo-blue.png",
      name: "Visualaey",
      description: plan.plan,
      order_id: order.order.id,

      handler: async function (response) {
        const verifyRes = await fetch(`${server}/Payment/ConfirmPayment`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: localStorage.getItem("authToken"),
          },
          body: JSON.stringify({
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_order_id: response.razorpay_order_id,
            razorpay_signature: response.razorpay_signature,
            userObjectId: user._id,
            membershipId: plan._id,
          }),
        });

        const verify = await verifyRes.json();
        setPayingPlanId(null);

        if (verify?.message?.toLowerCase().includes("verified")) {
          toast.success("Payment Successful, Plan Subscribed");
          navigate("/dashboard");
        } else {
          toast.error("Payment Verification Failed");
        }
      },

      modal: {
        ondismiss: () => {
          setPayingPlanId(null);
          toast.info("Payment Cancelled");
        },
      },

      theme: { color: "#ffffff" },
    };

    const rzp = new window.Razorpay(options);
    rzp.open();

    rzp.on("payment.failed", () => {
      setPayingPlanId(null);
      toast.error("Payment Failed");
    });
  };

  const nonFeatureFields = [
    "_id",
    "plan",
    "price",
    "planDescription",
    "category",
    "duration",
    "__v",
    "createdAt",
    "updatedAt",
  ];

  const featureKeys = regularPlans.length
    ? Object.keys(regularPlans[0]).filter(
        (key) => !nonFeatureFields.includes(key),
      )
    : [];

  const formatLabel = (key) =>
    key.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase());

  const renderValue = (value) => {
    if (typeof value === "boolean") {
      return value ? (
        <i className="bi bi-check fs-3 text-success"></i>
      ) : (
        <i className="bi bi-x fs-3 text-danger"></i>
      );
    }
    return value?.toString() || "-";
  };

  useEffect(() => {
    if (plans.length === 0) {
      dispatch(fetchMemberships());
    }
  }, [dispatch, plans.length]);

  const PlanCard = ({ plan }) => {
    const offerUsd = inrToUsd(plan.offerPrice);
    const actualUsd = inrToUsd(plan.actualPrice);

    const gstAmount = getGstAmount(plan.offerPrice);
    const totalInr = getTotalWithGst(plan.offerPrice);
    const totalUsd = inrToUsd(totalInr);

    const discountPercent =
      plan.actualPrice > plan.offerPrice
        ? Math.round(
            ((plan.actualPrice - plan.offerPrice) / plan.actualPrice) * 100,
          )
        : 0;

    const isPopular = plan?.isPopular; // optional (add in API later if you want)

    return (
      <div className="col-lg-4">
        <div
          className={`pro-card card h-100 rounded-5 border-0 ${
            isPopular ? "pro-card-popular" : ""
          }`}
        >
          <div className="card-body p-5 d-flex flex-column">
            {/* Badge */}
            {plan.badge && (
              <div className="pro-badge bg-warning text-dark px-5">
                {plan.badge}
              </div>
            )}

            {/* Header */}
            <div className="mb-4">
              <h3 className="fw-semibold text-info mb-2">{plan.plan}</h3>
              <p className="text-dark-50 mb-0 lh-sm">{plan.planDescription}</p>
            </div>

            {/* Price */}
            <div className="mb-4">
              <div className="d-flex flex-column align-items-start flex-wrap">
                <div className="d-flex align-items-end gap-2">
                  <span className="pro-price text-dark">
                    ₹{Math.round(plan.offerPrice / 12).toLocaleString("en-IN")}
                  </span>
                  <span className="text-dark-50 fw-medium mb-1">/month</span>
                </div>
                <div className="text-dark-50">
                  ₹{plan.offerPrice.toLocaleString("en-IN")} (
                  {formatUSD(offerUsd)}) billed annually
                </div>
              </div>

              <div className="bg-dark-soft py-3 px-4 rounded-4 mt-3">
                {plan.actualPrice > plan.offerPrice && (
                  <div className="d-flex align-items-center gap-3">
                    <span className="pro-strike text-dark-50">
                      ₹{plan.actualPrice.toLocaleString("en-IN")} (
                      {formatUSD(actualUsd)})
                    </span>
                    <span className="pro-discount text-success px-3 py-0 fw-semibold">
                      Save {discountPercent}%
                    </span>
                  </div>
                )}

                <div className="text-dark-50">
                  GST ₹{gstAmount.toLocaleString("en-IN")} &nbsp;•&nbsp; Total:{" "}
                  <span className="text-dark fw-semibold">
                    ₹{totalInr.toLocaleString("en-IN")} ({formatUSD(totalUsd)})
                  </span>
                </div>
              </div>
            </div>

            {/* Features */}
            <div className="pro-features rounded-4 px-2 py-0 mb-4">
              <ul className="list-unstyled mb-0">
                <li className="d-flex align-items-center gap-2 text-dark">
                  <i className="bi bi-check-circle-fill text-success"></i>
                  <span>{plan.eventLimit} Event(s)</span>
                </li>
                <li className="d-flex align-items-center gap-2 text-dark">
                  <i className="bi bi-check-circle-fill text-success"></i>
                  <span>{plan.photoLimit.toLocaleString()} Photos</span>
                </li>

                {plan.crownBadgeProfile && (
                  <li className="d-flex align-items-center gap-2 text-dark">
                    <i className="bi bi-check-circle-fill text-success"></i>
                    <span>Crown Badge Profile</span>
                  </li>
                )}

                {plan.dedicatedSupport && (
                  <li className="d-flex align-items-center gap-2 text-dark">
                    <i className="bi bi-check-circle-fill text-success"></i>
                    <span>Dedicated Support</span>
                  </li>
                )}
              </ul>
            </div>

            {/* CTA */}
            <button
              onClick={() => {
                if (!user?._id) {
                  localStorage.setItem(
                    "authRedirectIntent",
                    JSON.stringify({ target: location.pathname }),
                  );
                  navigate("/login");
                  return;
                }
                setSelectedPlan(plan);
                setCouponCode("");
                setShowCouponModal(true);
              }}
              className="btn pro-cta rounded-pill fw-semibold mt-auto bg-light py-2"
              disabled={payingPlanId === plan._id}
            >
              {payingPlanId === plan._id
                ? "Opening Payment Gateway..."
                : `Choose ${plan.plan}`}
            </button>

            <div className="text-center mt-2 text-dark-50 small">
              Secure checkout with Razorpay
            </div>
          </div>
        </div>
      </div>
    );
  };

  const ComparePlans = ({ plansToCompare, title = "Compare Plans" }) => {
    if (!plansToCompare?.length) return null;

    // These fields are NOT features
    const nonFeatureFields = [
      "_id",
      "plan",
      "price",
      "planDescription",
      "category",
      "duration",
      "__v",
      "createdAt",
      "updatedAt",
    ];

    // Build feature keys from union of all plans (more reliable than only [0])
    const featureKeys = Array.from(
      new Set(
        plansToCompare.flatMap((p) =>
          Object.keys(p).filter((k) => !nonFeatureFields.includes(k)),
        ),
      ),
    );

    const formatLabel = (key) =>
      key.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase());

    const renderCell = (value) => {
      if (typeof value === "boolean") {
        return value ? (
          <span className="cmp-icon cmp-yes">
            <i className="bi bi-check-lg"></i>
          </span>
        ) : (
          <span className="cmp-icon cmp-no">
            <i className="bi bi-x-lg"></i>
          </span>
        );
      }
      if (value === null || value === undefined || value === "")
        return <span className="text-muted">—</span>;
      return <span className="fw-semibold">{value.toString()}</span>;
    };

    return (
      <section className="pb-7 mt-n2 bg-white">
        <div className="container">
          <div className="d-flex align-items-end justify-content-between flex-wrap gap-2 mb-4">
            <div>
              <h2 className="fw-semibold mb-0">{title}</h2>
              <p className="text-muted mb-0 lh-1">
                Compare features across plans and choose what fits best.
              </p>
            </div>
          </div>

          <div className="cmp-wrap rounded-4 border">
            <div className="table-responsive cmp-table-scroll">
              <table className="table mb-0 cmp-table align-middle">
                <thead className="cmp-thead">
                  <tr>
                    <th className="cmp-sticky-col cmp-feature-col ps-5">
                      Features
                    </th>

                    {plansToCompare.map((p) => (
                      <th
                        key={p._id}
                        className={`text-center cmp-plan-col ${
                          p?.isPopular ? "cmp-popular-head" : ""
                        }`}
                      >
                        {p?.isPopular && (
                          <div className="cmp-popular-badge">Most Popular</div>
                        )}
                        <div className="fw-bold fs-5">{p.plan}</div>
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {featureKeys.map((key) => (
                    <tr key={key}>
                      <td className="cmp-sticky-col cmp-feature-col ps-5">
                        <div className="fw-medium text-dark">
                          {formatLabel(key)}
                        </div>
                      </td>

                      {plansToCompare.map((p) => (
                        <td key={p._id + key} className="text-center">
                          {renderCell(p[key])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>
    );
  };

  return (
    <>
      <Header />

      <section className="pt-5 pb-1 bg-image-pricing">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8 col-md-12 pt-7 pb-2">
              <div className="position-relative z-3 d-flex flex-column align-items-center justify-content-center">
                <h1 className="display-5 ls-sm m-0 fw-semibold text-white text-center">
                  Simple, Transparent Pricing for Every Event
                </h1>
                <p className="mb-5 display-6 lh-1 mt-2 text-white ls-sm text-center fw-normal">
                  From small gatherings to large celebrations, Visualaey scales
                  to fit your needs. Start with your first 3 events free, and
                  upgrade only when you're ready.
                </p>
                <div className="bg-danger rounded-pill py-1 px-4 mb-3">
                  <p className="text-white text-center lh-sm m-0 p-1">
                    ✨ Enjoy our introductory prices as we launch, lock in your
                    plan before prices increase.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="d-flex justify-content-center position-relative z-1 pb-8 mb-7">
            <span
              onClick={() => {
                setActiveTab("Regular");
                navigate("/pricing");
              }}
              className={`btn border-1 border-light fw-normal px-5 rounded-pill ${
                activeTab === "Regular" ? "bg-light text-dark" : "text-white"
              }`}
            >
              Regular Plans
            </span>
            <span
              onClick={() => {
                setActiveTab("Basic");
                navigate("/pricing/event-plan");
              }}
              className={`btn border-1 border-light fw-normal ms-2 px-5 rounded-pill ${
                activeTab === "Basic" ? "bg-light text-dark" : "text-white"
              }`}
            >
              Event Plans
            </span>
          </div>
        </div>
      </section>

      {!loading && (
        <section className="py-8 bg-white">
          <div className="container">
            <div className="row g-2">
              {activeTab === "Regular" &&
                regularPlans.map((plan) => (
                  <PlanCard key={plan._id} plan={plan} />
                ))}

              {activeTab === "Basic" &&
                basicPlans.map((plan) => (
                  <PlanCard key={plan._id} plan={plan} />
                ))}
            </div>
          </div>
        </section>
      )}

      {!loading && (
        <>
          <ComparePlans
            plansToCompare={activeTab === "Regular" ? regularPlans : basicPlans}
            title={
              activeTab === "Regular"
                ? "Compare Regular Plans"
                : "Compare Event Plans"
            }
          />
        </>
      )}

      <section>
        <div className="container pt-8 mb-lg-7 mt-lg-0 mt-7">
          <div className="row pt-2">
            <div className="col-xl-6 col-lg-10 col-12">
              <div className="mb-5 pe-8">
                <h2 className="display-5 fw-bold text-white mb-3 lh-1">
                  Frequently asked
                  <br />
                  questions, <span className="text-primary">answered.</span>
                </h2>
                <p className="fs-4 text-light opacity-75">
                  More questions? Visit the <a href="#">Help Center.</a>
                </p>
              </div>
            </div>
          </div>

          <div class="row">
            <div class="col-lg-8 col-12 pb-lg-0 pb-3 d-flex align-items-center">
              <div
                class="accordion accordion-flush w-100 pe-lg-5 pe-0 me-lg-5 me-0"
                id="accordionFlushExample"
              >
                <div class="accordion-item mt-1">
                  <h2 class="accordion-header">
                    <button
                      class="accordion-button px-0 bg-light px-5 rounded-pill"
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target="#flush-collapseOne"
                      aria-expanded="true"
                      aria-controls="flush-collapseOne"
                    >
                      <h3 class="fs-4 text-dark fw-medium m-0">
                        How does Visualaey work?
                      </h3>
                    </button>
                  </h2>
                  <div
                    id="flush-collapseOne"
                    class="accordion-collapse collapse"
                    data-bs-parent="#accordionFlushExample"
                  >
                    <div class="accordion-body px-5">
                      <p class="fs-4 fw-light mb-4 text-light opacity-75 lh-sm">
                        Visualaey uses facial recognition and deep learning to
                        identify individuals in event photos. Guests simply
                        upload a selfie, and our AI instantly finds and collects
                        every image they appear in—no manual tagging required.
                      </p>
                    </div>
                  </div>
                </div>
                <div class="accordion-item mt-1">
                  <h2 class="accordion-header">
                    <button
                      class="accordion-button px-0 bg-light px-5 rounded-pill collapsed"
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target="#flush-collapseThree"
                      aria-expanded="false"
                      aria-controls="flush-collapseThree"
                    >
                      <h3 class="fs-4 text-dark fw-medium m-0">
                        Is the platform secure and private?
                      </h3>
                    </button>
                  </h2>
                  <div
                    id="flush-collapseThree"
                    class="accordion-collapse collapse show"
                    data-bs-parent="#accordionFlushExample"
                  >
                    <div class="accordion-body px-5">
                      <p class="fs-4 fw-light mb-4 text-light opacity-75 lh-sm">
                        Yes. We use industry-standard security protocols (OAuth
                        2.0 / JWT) and end-to-end encryption to protect your
                        data. Facial data is processed securely and never shared
                        or sold.
                      </p>
                    </div>
                  </div>
                </div>
                <div class="accordion-item mt-1">
                  <h2 class="accordion-header">
                    <button
                      class="accordion-button px-0 bg-light px-5 rounded-pill collapsed"
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target="#flush-collapseTwo"
                      aria-expanded="false"
                      aria-controls="flush-collapseTwo"
                    >
                      <h3 class="fs-4 text-dark fw-medium m-0">
                        What types of events is Visualaey best for?
                      </h3>
                    </button>
                  </h2>
                  <div
                    id="flush-collapseTwo"
                    class="accordion-collapse collapse"
                    data-bs-parent="#accordionFlushExample"
                  >
                    <div class="accordion-body px-5">
                      <p class="fs-4 fw-light mb-4 text-light opacity-75 lh-sm">
                        Visualaey is perfect for weddings, parties, birthdays,
                        reunions, corporate events, festivals, and more—anywhere
                        multiple photos and people are involved.
                      </p>
                    </div>
                  </div>
                </div>
                <div class="accordion-item mt-1">
                  <h2 class="accordion-header">
                    <button
                      class="accordion-button px-0 bg-light px-5 rounded-pill collapsed"
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target="#flush-collapseFour"
                      aria-expanded="false"
                      aria-controls="flush-collapseFour"
                    >
                      <h3 class="fs-4 text-dark fw-medium m-0">
                        How many events can I create for free?
                      </h3>
                    </button>
                  </h2>
                  <div
                    id="flush-collapseFour"
                    class="accordion-collapse collapse"
                    data-bs-parent="#accordionFlushExample"
                  >
                    <div class="accordion-body px-5">
                      <p class="fs-4 fw-light mb-4 text-light opacity-75 lh-sm">
                        Your first 3 events are completely free—with full access
                        to all features. After that, you can upgrade to a
                        simple, flexible subscription plan.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-lg-4 col-12 d-flex align-items-center">
              <img
                src="/assets/images/6.jpg"
                className="w-100 rounded-5 object-fit-cover mt-n8 transform-rotateY d-lg-block d-none"
                height="400"
              />
            </div>
          </div>
        </div>
      </section>

      {showCouponModal && (
        <div
          className="modal fade show d-block"
          style={{ background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div
              className="coupon-card modal-content bg-dark px-0"
              style={{ background: "transparent", border: "none" }}
            >
              {/* Header */}
              <div
                style={{ padding: "1.75rem 1.75rem 0", position: "relative" }}
              >
                <button
                  style={{
                    position: "absolute",
                    top: "1.25rem",
                    right: "1.25rem",
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    background: "rgba(255,255,255,0.06)",
                    border: "1px solid rgba(255,255,255,0.1)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    color: "rgba(255,255,255,0.5)",
                    fontSize: 16,
                  }}
                  onClick={() => setShowCouponModal(false)}
                >
                  ✕
                </button>

                <div className="coupon-icon-badge">🎟️</div>
                <div className="coupon-savings-badge px-4">
                  ✦ Save up to 80%
                </div>

                <h4
                  style={{
                    fontFamily: "'Sora', sans-serif",
                    fontSize: 22,
                    fontWeight: 700,
                    color: "#fff",
                    margin: "0 0 6px",
                    lineHeight: 1.2,
                  }}
                >
                  Apply coupon code
                </h4>
                <p
                  style={{
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: 14,
                    color: "rgba(255,255,255,0.45)",
                    margin: "0 0 1.5rem",
                    lineHeight: 1.3,
                  }}
                >
                  Have a coupon? Enter it below and get your instant discount at
                  checkout.
                </p>
              </div>

              {/* Divider */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  margin: "0 1.75rem",
                }}
              >
                <div
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    background: "rgba(255,255,255,0.08)",
                  }}
                />
                <div
                  style={{
                    flex: 1,
                    height: 1,
                    background: "rgba(255,255,255,0.06)",
                  }}
                />
                <div
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    background: "rgba(255,255,255,0.08)",
                  }}
                />
              </div>

              {/* Body */}
              <div style={{ padding: "1.25rem 1.75rem" }}>
                <div className="coupon-input-wrap">
                  <i className="ti ti-ticket icon-left" />
                  <input
                    type="text"
                    className="coupon-field"
                    placeholder="Enter Coupon Code"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value)}
                    autoComplete="off"
                  />
                  <i className="ti ti-check icon-right" />
                </div>
                <p className="coupon-hint">
                  <i className="ti ti-lock" style={{ fontSize: 13 }} />
                  Coupon codes are case-insensitive
                </p>
              </div>

              {/* Footer */}
              <div
                style={{
                  padding: "0.5rem 1.75rem 1.75rem",
                  display: "flex",
                  gap: 10,
                }}
              >
                <button
                  className="btn-coupon-skip"
                  onClick={() => proceedToPayment(false)}
                >
                  Skip
                </button>
                <button
                  className="btn-coupon-apply"
                  onClick={() => proceedToPayment(true)}
                >
                  ✦ Apply &amp; continue
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </>
  );
}

export default Pricing;
